import { ArrowDownUp, CalendarDays, CalendarRange, Clock3, Download, UserRound } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { WorkspaceSkeleton } from '../components/UI'
import { useTeamScope } from '../components/TeamScope'
import {
  formatDuration,
  loadOrganisationEmployeeHistory,
  loadOrganisationHistory,
  readableDataError,
  type DailyOperatingSummary,
  type EmployeeDailyRecord,
  type HistoryEmployee
} from '../lib/liveOperations'

type HistoryView = 'daily' | 'weekly' | 'monthly'
type SortOrder = 'newest' | 'oldest'
type HistoryScope = 'organisation' | 'employee'
type EmployeePeriod = {
  key: string
  userId: string
  displayName: string
  teamName: string
  records: EmployeeDailyRecord[]
}

const dayKey = (value: string) => String(value).slice(0, 10)

function dayLabel(value: string) {
  return new Date(`${dayKey(value)}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

function monthKey(value: string) {
  return dayKey(value).slice(0, 7)
}

function monthLabel(value: string) {
  const [year, month] = value.split('-').map(Number)
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric'
  })
}

function weekOfMonth(value: string) {
  return Math.floor((Number(dayKey(value).slice(8, 10)) - 1) / 7) + 1
}

function weekKey(value: string) {
  return `${monthKey(value)}-W${weekOfMonth(value)}`
}

function weekLabel(value: string) {
  const month = value.slice(0, 7)
  const week = Number(value.split('-W')[1])
  return `Week ${week} · ${monthLabel(month)}`
}

function average(values: Array<number | null | undefined>) {
  const numbers = values.filter((value): value is number => value != null && Number.isFinite(value))
  return numbers.length ? Math.round(numbers.reduce((sum, value) => sum + value, 0) / numbers.length) : null
}

function organisationGroups(rows: DailyOperatingSummary[], view: HistoryView) {
  if (view === 'daily') return rows.map(row => ({ key: dayKey(row.operating_day), rows: [row] }))

  const grouped = new Map<string, DailyOperatingSummary[]>()
  for (const row of rows) {
    const key = view === 'weekly' ? weekKey(row.operating_day) : monthKey(row.operating_day)
    grouped.set(key, [...(grouped.get(key) ?? []), row])
  }
  return [...grouped.entries()].map(([key, groupedRows]) => ({ key, rows: groupedRows }))
}

function employeeGroups(records: EmployeeDailyRecord[], people: HistoryEmployee[], view: HistoryView): EmployeePeriod[] {
  const personByUser = new Map(people.map(person => [person.userId, person]))
  const grouped = new Map<string, EmployeePeriod>()

  for (const record of records) {
    const period = view === 'daily' ? dayKey(record.activity_day) : view === 'weekly' ? weekKey(record.activity_day) : monthKey(record.activity_day)
    const compoundKey = `${record.user_id}:${period}`
    const person = personByUser.get(record.user_id)
    const item = grouped.get(compoundKey) ?? {
      key: period,
      userId: record.user_id,
      displayName: person?.displayName ?? 'Team member',
      teamName: person?.teamName ?? 'Team',
      records: []
    }
    item.records.push(record)
    grouped.set(compoundKey, item)
  }

  return [...grouped.values()]
}

function periodLabel(view: HistoryView, key: string) {
  if (view === 'daily') return dayLabel(key)
  if (view === 'weekly') return weekLabel(key)
  return monthLabel(key)
}

function organisationSummary(rows: DailyOperatingSummary[]) {
  return {
    received: rows.reduce((sum, row) => sum + Number(row.inbound_threads || 0), 0),
    replies: rows.reduce((sum, row) => sum + Number(row.threads_replied || 0), 0),
    people: Math.max(0, ...rows.map(row => Number(row.active_people || 0))),
    response: average(rows.map(row => row.average_arrival_to_reply_sent_seconds))
  }
}

function employeeSummary(records: EmployeeDailyRecord[]) {
  const replies = records.filter(record => record.work_status === 'replied').length
  return {
    worked: records.length,
    replies,
    responseRate: records.length ? Math.round((replies / records.length) * 100) : null,
    response: average(records.map(record => record.arrival_to_reply_sent_seconds))
  }
}

export function HistoryRecords() {
  const { selectedTeamId, selectedTeam } = useTeamScope()
  const [view, setView] = useState<HistoryView>('daily')
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest')
  const [scope, setScope] = useState<HistoryScope>('organisation')
  const [organisationRows, setOrganisationRows] = useState<DailyOperatingSummary[]>([])
  const [employeeRows, setEmployeeRows] = useState<EmployeeDailyRecord[]>([])
  const [people, setPeople] = useState<HistoryEmployee[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    setError('')
    setOrganisationRows([])
    setEmployeeRows([])
    setPeople([])
    Promise.allSettled([loadOrganisationHistory(selectedTeamId), loadOrganisationEmployeeHistory(selectedTeamId)])
      .then(results => {
        const reasons: string[] = []

        if (results[0].status === 'fulfilled') setOrganisationRows(results[0].value)
        else reasons.push(readableDataError(results[0].reason, 'Organisation records could not be loaded.'))

        if (results[1].status === 'fulfilled') {
          setEmployeeRows(results[1].value.records)
          setPeople(results[1].value.people)
        } else {
          reasons.push(readableDataError(results[1].reason, 'Employee records could not be loaded.'))
        }

        setError(reasons.join(' '))
      })
      .finally(() => setLoading(false))
  }, [selectedTeamId])

  const organisationPeriods = useMemo(() =>
    organisationGroups(organisationRows, view).sort((a, b) =>
      sortOrder === 'newest' ? b.key.localeCompare(a.key) : a.key.localeCompare(b.key)
    ), [organisationRows, view, sortOrder])

  const employeePeriods = useMemo(() =>
    employeeGroups(employeeRows, people, view).sort((a, b) => {
      const periodOrder = sortOrder === 'newest' ? b.key.localeCompare(a.key) : a.key.localeCompare(b.key)
      return periodOrder || a.displayName.localeCompare(b.displayName)
    }), [employeeRows, people, view, sortOrder])

  if (loading) return <WorkspaceSkeleton rows={6}/>

  return <div className="stack-lg history-page">
    <div className="history-page-intro">
      <div>
        <span className="eyebrow">Organisation and employee records</span>
        <h2>History</h2>
        <p>Browse past operating reports for {selectedTeam ? selectedTeam.name : 'the organisation as a whole'} or for individual employees.</p>
      </div>
      <div className="history-actions">
        <button className="button secondary" onClick={() => setSortOrder(value => value === 'newest' ? 'oldest' : 'newest')}>
          <ArrowDownUp size={16}/>{sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
        </button>
        <button className="button secondary" onClick={() => window.print()}><Download size={16}/>Export view</button>
      </div>
    </div>

    {error && <div className="notice notice-inline"><strong>Some history is unavailable:</strong><span>{error}</span></div>}

    <div className="history-view-tabs">
      <button className={view === 'daily' ? 'active' : ''} onClick={() => setView('daily')}><CalendarDays size={17}/><span>Daily</span></button>
      <button className={view === 'weekly' ? 'active' : ''} onClick={() => setView('weekly')}><CalendarRange size={17}/><span>Weekly</span></button>
      <button className={view === 'monthly' ? 'active' : ''} onClick={() => setView('monthly')}><Clock3 size={17}/><span>Monthly</span></button>
    </div>

    <div className="history-scope-tabs" role="tablist" aria-label="History report type">
      <button
        role="tab"
        aria-selected={scope === 'organisation'}
        className={scope === 'organisation' ? 'active' : ''}
        onClick={() => setScope('organisation')}
      >
        <span>Overall reports</span>
        <small>{organisationPeriods.length}</small>
      </button>

      <button
        role="tab"
        aria-selected={scope === 'employee'}
        className={scope === 'employee' ? 'active' : ''}
        onClick={() => setScope('employee')}
      >
        <span>Employee reports</span>
        <small>{employeePeriods.length}</small>
      </button>
    </div>

    {scope === 'organisation' ? (
      <section className="history-section">
        <div className="history-section-heading">
          <div>
            <span className="eyebrow">Overall records</span>
            <h3>{selectedTeam ? `${selectedTeam.name} ${view} reports` : `Organisation ${view} reports`}</h3>
          </div>
          <span>{organisationPeriods.length} report{organisationPeriods.length === 1 ? '' : 's'}</span>
        </div>

        {organisationPeriods.length ? <div className="history-report-grid">
          {organisationPeriods.map(period => {
            const summary = organisationSummary(period.rows)
            return <Link
              key={period.key}
              className="history-report-link"
              to={`/app/history/organisation/${view}/organisation/${encodeURIComponent(period.key)}`}
            >
              <div className="history-report-link-head">
                <div><span>{view} report</span><strong>{periodLabel(view, period.key)}</strong></div>
                <CalendarDays size={18}/>
              </div>

              <div className="history-report-link-stats">
                <div><span>Emails received</span><strong>{summary.received}</strong></div>
                <div><span>Replies sent</span><strong>{summary.replies}</strong></div>
                <div><span>Employees active</span><strong>{summary.people}</strong></div>
                <div><span>Average response</span><strong>{formatDuration(summary.response)}</strong></div>
              </div>

              <span className="history-open-report">Open report →</span>
            </Link>
          })}
        </div> : (
          <div className="history-empty-line">No organisation {view} reports are available yet.</div>
        )}
      </section>
    ) : (
      <section className="history-section">
        <div className="history-section-heading">
          <div>
            <span className="eyebrow">Individual records</span>
            <h3>Employee {view} reports</h3>
          </div>
          <span>{employeePeriods.length} report{employeePeriods.length === 1 ? '' : 's'}</span>
        </div>

        {employeePeriods.length ? <div className="history-report-grid">
          {employeePeriods.map(period => {
            const summary = employeeSummary(period.records)
            return <Link
              key={`${period.userId}:${period.key}`}
              className="history-report-link"
              to={`/app/history/employee/${view}/${period.userId}/${encodeURIComponent(period.key)}`}
            >
              <div className="history-report-link-head">
                <div><span>{period.teamName}</span><strong>{period.displayName}</strong></div>
                <UserRound size={18}/>
              </div>

              <p className="history-period-name">{periodLabel(view, period.key)}</p>

              <div className="history-report-link-stats">
                <div><span>Emails worked</span><strong>{summary.worked}</strong></div>
                <div><span>Replies sent</span><strong>{summary.replies}</strong></div>
                <div><span>Response rate</span><strong>{summary.responseRate == null ? '—' : `${summary.responseRate}%`}</strong></div>
                <div><span>Average response</span><strong>{formatDuration(summary.response)}</strong></div>
              </div>

              <span className="history-open-report">Open employee report →</span>
            </Link>
          })}
        </div> : (
          <div className="history-empty-line">No employee {view} reports are available yet.</div>
        )}
      </section>
    )}
  </div>
}
